import * as pdfjsLib from "pdfjs-dist";

// Dynamically configure the worker source using the version from the loaded library.
// This avoids "import ... ?url" syntax errors in environments using importmaps/CDNs.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://aistudiocdn.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * Extracts raw text from a PDF file using pdf.js in the browser.
 * This avoids runtime issues with PDF parsing in Cloudflare Workers.
 */
export async function extractTextFromPdf(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();

    // Load the PDF document
    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
      useSystemFonts: true,
      disableFontFace: true,
    });

    const pdf = await loadingTask.promise;
    let fullText = "";

    // Iterate through all pages
    // Limit to first 50 pages to prevent browser freeze on massive documents
    const maxPages = Math.min(pdf.numPages, 50);

    for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      // Extract string items
      const pageText = (textContent.items as any[])
        .map((item: any) => item.str)
        .join(" "); // Join words with space

      fullText += `[Page ${pageNum}]\n${pageText}\n\n`;
    }

    if (pdf.numPages > maxPages) {
      fullText += `\n... (剩餘 ${pdf.numPages - maxPages} 頁內容未讀取) ...`;
    }

    return fullText.trim();
  } catch (error: any) {
    console.error("PDF Extraction Failed:", error);
    throw new Error(`PDF 解析失敗: ${error?.message || String(error)}`);
  }
}

export type RenderPdfFirstPageOptions = {
  /** Maximum rendered width in pixels (keeps aspect ratio). Default: 1400 */
  maxWidth?: number;
  /** JPEG quality 0~1. Default: 0.85 */
  jpegQuality?: number;

  // ---- Legacy/compat options (some callers use these names) ----
  /** Legacy: scale factor. If provided, it will be used unless maxDim/maxWidth forces downscale. */
  scale?: number;
  /** Legacy: max dimension (px) for the longer edge. */
  maxDim?: number;
  /** Legacy: alias of jpegQuality */
  quality?: number;
};

/**
 * Render the first page of a PDF as a JPEG data URL.
 *
 * Used as OCR (vision) fallback when the PDF has no text layer (scanned PDF).
 */
export async function renderPdfFirstPageAsJpegDataUrl(
  file: File,
  options: RenderPdfFirstPageOptions = {}
): Promise<string> {
  try {
    // This helper must run in browser (needs canvas/document).
    if (typeof document === "undefined") {
      throw new Error("renderPdfFirstPageAsJpegDataUrl must run in browser");
    }

    const maxWidthFromMaxDim =
      typeof options.maxDim === "number" && options.maxDim > 0
        ? options.maxDim
        : undefined;

    // Prefer modern option names, but allow legacy aliases.
    const maxWidth =
      typeof options.maxWidth === "number" && options.maxWidth > 0
        ? options.maxWidth
        : typeof maxWidthFromMaxDim === "number"
        ? maxWidthFromMaxDim
        : 1400;

    const jpegQuality =
      typeof options.jpegQuality === "number"
        ? options.jpegQuality
        : typeof options.quality === "number"
        ? options.quality
        : 0.85;

    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
      useSystemFonts: true,
      disableFontFace: true,
    });

    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);

    // Start from the requested base scale (legacy), then downscale to fit maxWidth.
    const baseScale = typeof options.scale === "number" && options.scale > 0 ? options.scale : 1;
    const viewportAtBase = page.getViewport({ scale: baseScale });
    const scale = viewportAtBase.width > maxWidth ? (maxWidth / viewportAtBase.width) * baseScale : baseScale;

    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context not available");

    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);

    const renderTask = page.render({
      canvasContext: ctx as any,
      viewport: viewport as any,
    });
    await renderTask.promise;

    const dataUrl = canvas.toDataURL("image/jpeg", jpegQuality);

    // Best-effort cleanup
    canvas.width = 0;
    canvas.height = 0;

    return dataUrl;
  } catch (error: any) {
    console.error("PDF First Page Render Failed:", error);
    throw new Error(`PDF 首頁渲染失敗: ${error?.message || String(error)}`);
  }
}
